/**
 * Data layer for `appsflyer-network-postback-installs-in-app-events`.
 * Thin typed fetch wrappers over /api/v1/appsflyer-network-postback-installs-in-app-events.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface PostbackEventRow {
	id: number;
	tenant_id: string;
	af_event_id: string;
	event_name: string;
	event_time: number;
	app_id: string | null;
	af_siteid: string | null;
	advertising_id: string | null;
	idfa: string | null;
	idfv: string | null;
	gaid: string | null;
	event_revenue: number | null;
	event_revenue_currency: string | null;
	payload_json: string;
	received_at: number;
}

export interface ListPostbackEventsResponse {
	items: PostbackEventRow[];
}

export interface CreatePostbackEventBody {
	af_event_id: string;
	event_name?: string;
	event_time?: number | string;
	app_id?: string;
	af_siteid?: string;
	advertising_id?: string;
	idfa?: string;
	idfv?: string;
	gaid?: string;
	event_revenue?: number | string;
	event_revenue_currency?: string;
	[key: string]: unknown;
}

export interface CreatePostbackEventResponse {
	ok: boolean;
	idempotent: boolean;
	af_event_id: string;
	event_name: string;
	event_time: number;
	received_at: number;
}

export interface ListQueryParams {
	event_name?: string;
	limit?: number;
}

const BASE = "/api/v1/appsflyer-network-postback-installs-in-app-events";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	params: ListQueryParams = {},
): Promise<ListPostbackEventsResponse> {
	const qs = new URLSearchParams();
	if (params.event_name) qs.set("event_name", params.event_name);
	if (typeof params.limit === "number" && params.limit > 0) {
		qs.set("limit", String(Math.floor(params.limit)));
	}
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostbackEventsResponse>(res);
}

export async function create(
	body: CreatePostbackEventBody,
): Promise<CreatePostbackEventResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreatePostbackEventResponse>(res);
}

export const postbackEventsQueryKeys = {
	all: ["appsflyer-network-postback-installs-in-app-events"] as const,
	list: (params: ListQueryParams = {}) =>
		[
			...postbackEventsQueryKeys.all,
			"list",
			params.event_name ?? "",
			params.limit ?? 0,
		] as const,
	detail: (id: number) =>
		[...postbackEventsQueryKeys.all, id] as const,
};
